import { Breakpoint } from "@/types/breakpoints";
import { PropertiesData } from "@/types/tree";
import {
  BASE_BREAKPOINT_ID,
  FIRST_RESPONSIVE_BREAKPOINT_ID,
  mediaQueryString
} from "@/lib/breakpoints/breakpoints";
import { renderTags, renderTwig } from "@/lib/twig/twig";
import { configStore, globalStore } from "@/store/store";
import { getFlattenedPropertiesByBreakpoint } from "@/lib/canvas/flatten-properties";
import { BuilderElement } from "@/types/elements";
import { Tags } from "@/types/tags";

export function generateCss(
  properties: PropertiesData,
  breakpoints: Breakpoint[],
  elementCss: BuilderElement["cssTemplate"],
  tags: Tags,
  propertyPathsToWhitelistInFlatProps?: BuilderElement["propertyPathsToWhitelistInFlatProps"],
  childElementCount?: number,
  elementCssTemplateCacheKey?: string
) {
  let cssString = "";

  const propsToWhitelist = [
    ...configStore.variablesControlsAndTemplate
      .propertyPathsToWhitelistInFlatProps,
    ...(propertyPathsToWhitelistInFlatProps || [])
  ];

  for (const breakpoint of breakpoints) {
    const flattenedProps = getFlattenedPropertiesByBreakpoint(
      {
        ...properties,
        globalSettings: globalStore.globalSettings.settings,
        variables: globalStore.variables
      },
      breakpoint.id,
      configStore.breakpointIds,
      BASE_BREAKPOINT_ID,
      propsToWhitelist
    );

    const cssForBreakpoint = renderTwig(
      elementCss,
      {
        ...flattenedProps,
        isBaseBreakpoint: breakpoint.id === BASE_BREAKPOINT_ID,
        firstResponsiveBreakpointId: FIRST_RESPONSIVE_BREAKPOINT_ID,
        breakpoint: breakpoint.id,
        builtinBreakpoints: configStore.builtinBreakpoints,
        childElementCount: childElementCount
      },
      elementCssTemplateCacheKey
    );

    const query = mediaQueryString(breakpoint.minWidth, breakpoint.maxWidth);

    if (query) {
      cssString += `\n\n${query} {${cssForBreakpoint}}`;
    } else {
      cssString += cssForBreakpoint;
    }
  }

  const cssWithTags = renderTags(cssString, tags);
  const cssWithoutEmptyProperties = stripEmptyProperties(cssWithTags);
  const cssWithoutEmptyTags = stripEmptyTags(cssWithoutEmptyProperties);

  return cssWithoutEmptyTags;
}

/**
 * All these functions and code were copied from PHP's "minify" library.
 * Any changes must be made there too
 *
 * For better comments and explanations, see the PHP version
 */
function stripEmptyProperties(content: string) {
  return content.replace(/[a-zA-Z0-9-_:().#]*:\s*;/g, "");
}

function stripEmptyTags(content: string) {
  // Safari doesn't support regex lookbehind: https://github.com/soflyy/breakdance/issues/3594
  try {
    content = stripEmptySelectors(content);
    content = stripEmptySelectors(content);
    content = stripEmptySelectorsAtTheStartOfTheFile(content);
  } catch (e) {
    // do nothing
  }

  return content;
}

function stripEmptySelectorsAtTheStartOfTheFile(content: string) {
  // disable ESlint here as I want the regex to be exactly the same as PHP's
  // eslint-disable-next-line no-useless-escape
  return content.replace(new RegExp("(?<=^)[^\\{\\};]+\\{\\s*\\}", "g"), "");
}

function stripEmptySelectors(content: string) {
  // disable ESlint here as I want the regex to be exactly the same as PHP's
  // eslint-disable-next-line no-useless-escape
  return content.replace(
    new RegExp("(?<=(\\}|;|\\{))[^\\{\\};]+\\{\\s*\\}", "g"),
    ""
  );
}

/*****/
